/* eslint-disable no-param-reassign */
import _, { groupBy, isString, compact, difference, forEach } from 'underscore';
import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { makeStyles, tokens, Checkbox, mergeClasses } from '@fluentui/react-components';
import { ContextualClientSelectionTree } from '@bingads-webui-fluent-component/selection-tree';
import { Tags } from '@bingads-webui-fluent-component/tags';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { extendListSizeItem, extendInternalizationReach, getLocalizedMarkets, formatReachEstimation } from '@bingads-webui-campaign-react/ima-reach-representation';

import { Estimation } from './estimation';

const useStyles = makeStyles({
  searchBox: {
    maxWidth: '100%',
    '& input[type="search"]': {
      paddingLeft: tokens.spacingHorizontalSNudge,
      backgroundImage: 'none',
    },
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    marginRight: '5px',
    cursor: 'default',
  },
  checkboxCursor: {
    cursor: 'pointer',
  },
});

export const InterestsSelector = ({
  locationTargets,
  treeItems,
  treeItemIdxMap,
  isAIMCampaignEnabled,
  campaignType,
  selectedItems,
  disabledItems,
  onChange,
  containerClassName,
  bundleLocationCriterions,
}) => {
  const i18n = useContext(I18nContext);
  const styles = useStyles();

  const treeItemsWithReach = useMemo(() => {
    if (!treeItems) {
      return undefined;
    }
    const isBundleSelected = locationTargets && locationTargets.targetOption === 'bundle';
    const locationTargetsWithBundleCountries = isBundleSelected ? bundleLocationCriterions : locationTargets.currentLocations;
    const { Included = [], Excluded = [] } = groupBy(locationTargetsWithBundleCountries, 'SelectionStatus');
    const targetedLocationCriterions = Included.map((location) => {
      const criterion = location.Criterion.Type === 'Location' ? location.Criterion.LocationCriterion : location.Criterion.RadiusCriterion;
      return criterion.CountryCode.toUpperCase();
    });
    const excludedLocationCriterions = Excluded.map((location) => {
      const criterion = location.Criterion.Type === 'Location' ? location.Criterion.LocationCriterion : location.Criterion.RadiusCriterion;
      return criterion.CountryCode.toUpperCase();
    });

    const processedList = treeItems.map((item) => {
      const result = cloneDeep(item);

      if (result.data) {
        if (result.data.InternalizationReach) {
          result.data = extendInternalizationReach({
            item: result.data,
            peopleInLocation: locationTargets.peopleInLocation,
            targetedLocationCriterions,
            excludedLocationCriterions,
            i18n,
          });
        }
        result.data = extendListSizeItem({
          item: result.data,
          isAIMCampaignEnabled,
          campaignType,
          i18n,
        });
      }
      return result;
    });

    return processedList;
  }, [treeItems, locationTargets, bundleLocationCriterions, isAIMCampaignEnabled, campaignType, i18n]);

  const ineligibleItems = useMemo(() => compact(treeItemsWithReach.map((item) => {
    if (item.data && item.data.isInEligibleIMA) {
      return item.value;
    }
    return null;
  })), [treeItemsWithReach]);

  // process disabled items
  const processedTreeItems = useMemo(() => {
    forEach(treeItemsWithReach, (item) => {
      item.disabled = false;
    });

    const queue = [...difference(ineligibleItems, selectedItems), ...disabledItems];
    while (queue.length > 0) {
      const item = queue.shift();
      const idx = treeItemIdxMap[item];
      const node = treeItemsWithReach[idx];
      node.disabled = true;
    }

    return treeItemsWithReach;
  }, [disabledItems, treeItemsWithReach, treeItemIdxMap, ineligibleItems, selectedItems]);

  const checkedItems = useMemo(() => {
    const checked = new Map();
    selectedItems.forEach(item => checked.set(item, true));

    return checked;
  }, [selectedItems]);

  const onTargetSelect = useCallback(({ item, data }) => {
    const isAddingTarget = data.checked;

    if (isAddingTarget) {
      onChange([...selectedItems, item.value]);
    } else {
      const removedTarget = selectedItems && selectedItems.filter(selected => selected !== item.value);
      onChange([...removedTarget]);
    }
  }, [onChange, selectedItems]);

  const onRenderFlatTreeItem = useCallback((item) => {
    const {
      name, data: itemData, value, disabled, index,
    } = item;

    const isItemChecked = checkedItems && checkedItems.has(value);
    const isDisabled = !isItemChecked && disabled;

    if (!itemData) {
      return (
        <div>{name}</div>
      );
    }

    const localizedMarkets = itemData.listSize === '-' ? [] : getLocalizedMarkets({
      item: itemData,
      i18n,
      campaignType,
    });
    const estimation = isString(itemData.listSize) ? itemData.listSize : formatReachEstimation(itemData.listSize, i18n);

    return (
      <div className={styles.item}>
        <div><Checkbox className={mergeClasses(`interest-tree-item-checkbox-${index}`, 'no-tree-open-close', `${isDisabled ? '' : styles.checkboxCursor}`)} disabled={isDisabled} checked={isItemChecked} onChange={(_e, data) => onTargetSelect({ item, data })} />{name}</div>
        <Estimation
          estimation={estimation}
          localizedMarkets={localizedMarkets}
        />
      </div>
    );
  }, [styles, i18n, campaignType, checkedItems, processedTreeItems]);

  const onRenderTagContent = useCallback((item) => {
    const idx = treeItemIdxMap[item];
    const node = treeItemsWithReach[idx];

    const { name } = node;
    return (<div>{name}</div>
    );
  }, [treeItemIdxMap, treeItemsWithReach]);

  const flatTreeItemFilter = useCallback((item, searchQuery) => {
    const name = item.name || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  }, []);

  return (
    <div className={`${containerClassName}-container`}>
      <Tags
        tagItems={selectedItems}
        onChange={onChange}
        onRenderTagContent={onRenderTagContent}
        i18n={i18n}
        tagViewLimit={10}
      />
      <ContextualClientSelectionTree
        i18n={i18n}
        containerClassName={containerClassName}
        placeholder={i18n.getString(_TL_('Search or browse interests'))}
        treeItems={processedTreeItems}
        onChange={_.noop}
        onRenderContent={onRenderFlatTreeItem}
        customFilter={flatTreeItemFilter}
        searchBoxClassName={styles.searchBox}
        selectionMode="none"
        showTags={false}
      />
    </div>
  );
};

InterestsSelector.propTypes = {
  locationTargets: PropTypes.shape({
    currentLocations: PropTypes.arrayOf(PropTypes.shape({})),
    peopleInLocation: PropTypes.number,
    targetOption: PropTypes.string,
  }).isRequired,
  treeItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  treeItemIdxMap: PropTypes.shape({}).isRequired,
  isAIMCampaignEnabled: PropTypes.bool.isRequired,
  campaignType: PropTypes.string.isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.string),
  disabledItems: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  containerClassName: PropTypes.string,
  bundleLocationCriterions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

InterestsSelector.defaultProps = {
  selectedItems: [],
  disabledItems: [],
  containerClassName: '',
};
